import { useState, type FunctionComponent } from "react";
import { CloseOutlined, PictureOutlined } from "@ant-design/icons";
import { Button, Tooltip, Upload, message } from "antd";
import { type RcFile } from "antd/es/upload";

import {
  ChallengePostMediaType,
  MAX_CHALLENGE_POST_MEDIA,
  type ChallengePostMedia,
} from "@fitness-app/data-models/entities/ChallengeFeed";
import { getParentTrainerId } from "@fitness-app/app-store/src/store/reducers/user/selectors";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { supabase, useAppSelector } from "~/store/initializeStore";

const MAX_UPLOAD_MB = 50;
const ACCEPTED = "image/png,image/jpeg,image/webp,video/mp4,video/quicktime";

/**
 * The extension ends up inside the storage key, so it is taken from the user-supplied name only
 * when it is plain alphanumerics — `a.b/c` or `x..` would otherwise smuggle a `/` or `..` into the
 * key and escape the `${parentId}/challenges/feed/` prefix the storage policies key off.
 */
const extensionFor = (file: RcFile): string => {
  const fromName = file.name.split(".").pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{1,5}$/.test(fromName)) {
    return fromName;
  }
  return file.type === "image/png" ? "png" : file.type.startsWith("video/") ? "mp4" : "jpg";
};

type Props = {
  challengeId: string;
  value: ChallengePostMedia[];
  onChange: (media: ChallengePostMedia[]) => void;
  disabled?: boolean;
  /** A comment gets fewer slots than a post — see `MAX_CHALLENGE_COMMENT_MEDIA`. */
  limit?: number;
};

/**
 * Upload trigger for a post's attachments — rendered as a toolbar icon, so it sits with the other
 * composer actions instead of stealing a whole row. Thumbnails live in {@link ChallengePostMediaPreview}.
 * Files go to the public `shared` bucket, same as the challenge covers.
 */
export const ChallengePostMediaUploadButton: FunctionComponent<Props> = ({
  challengeId,
  value,
  onChange,
  disabled,
  limit = MAX_CHALLENGE_POST_MEDIA,
}) => {
  const parentId = useAppSelector(getParentTrainerId);
  const [uploading, setUploading] = useState(false);
  const isFull = value.length >= limit;

  const upload = async (file: RcFile): Promise<void> => {
    if (isFull) {
      void message.error(`Maksymalnie ${limit} załączników`);
      return;
    }
    if (file.size / 1024 / 1024 > MAX_UPLOAD_MB) {
      void message.error(`Plik może mieć maksymalnie ${MAX_UPLOAD_MB}MB`);
      return;
    }

    setUploading(true);
    try {
      const path = `${parentId}/challenges/feed/${challengeId}_${generateUniqId()}.${extensionFor(file)}`;
      const { error } = await supabase.storage.from("shared").upload(path, file, {
        cacheControl: "3600",
        upsert: true,
      });
      if (error) {
        throw new Error(error.message);
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("shared").getPublicUrl(path);

      onChange([
        ...value,
        {
          url: publicUrl,
          type: file.type.startsWith("video/") ? ChallengePostMediaType.Video : ChallengePostMediaType.Image,
        },
      ]);
    } catch (error) {
      void message.error(error instanceof Error ? error.message : "Nie udało się wgrać pliku");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload
      accept={ACCEPTED}
      maxCount={1}
      showUploadList={false}
      disabled={disabled || uploading || isFull}
      customRequest={({ file }) => void upload(file as RcFile)}
    >
      <Tooltip title={isFull ? `Maksymalnie ${limit} załączników` : "Zdjęcie lub wideo"}>
        <Button type="text" icon={<PictureOutlined />} loading={uploading} disabled={disabled || isFull} />
      </Tooltip>
    </Upload>
  );
};

/** Attachment thumbnails with a remove affordance — sits under the body, above the toolbar. */
export const ChallengePostMediaPreview: FunctionComponent<{
  value: ChallengePostMedia[];
  onChange: (media: ChallengePostMedia[]) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  if (!value.length) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {value.map((item, index) => (
        <div key={item.url} className="relative">
          {item.type === ChallengePostMediaType.Image ? (
            <img src={item.url} alt="" className="h-20 w-20 rounded-lg object-cover" />
          ) : (
            <video src={item.url} className="h-20 w-28 rounded-lg bg-black object-cover" />
          )}
          <button
            type="button"
            aria-label="Usuń załącznik"
            disabled={disabled}
            onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
            className="absolute -right-2 -top-2 rounded-full bg-[rgba(0,0,0,0.65)] p-1 text-white"
          >
            <CloseOutlined style={{ fontSize: 10 }} />
          </button>
        </div>
      ))}
    </div>
  );
};
